// src/belediye/components/mesajlasma/NewChatPanel.js
// "Yeni sohbet" — kişi ara (DM), çoklu kişi ve/veya birim seç (grup). Birimler
// contacts ucundan yalnız mesajlasma.unit.send sahibine gelir (kurum içinde
// birime toplu yazmak bilinçli, öne çıkmaz). Grup izni yoksa yalnız tekli DM.
//
// G9: görev-önce modda (serbest kapalı + görev açık) sohbet BAŞLAMAZ — bu panel
// yalnız KİŞİ SEÇER. "Sohbet Başlat" → onStartTaskFlow ile görev-akışı THREAD
// (sohbet ekranı) alanında açılır; conversation ancak görev/başvuru oluşunca doğar.
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { ArrowLeftIcon, BuildingOffice2Icon } from "@heroicons/react/24/outline";

import { searchContacts, startDirect, startGroup } from "../../services/mesajlasmaApi";
import usePermissions from "../../hooks/usePermissions";
import useMsjFeatures from "./useMsjFeatures";
import rehberBolumleri, { rehberSatiri } from "./rehberGruplari";
import Avatar from "./Avatar";

export default function NewChatPanel({ onStarted, onCancel, onStartTaskFlow }) {
  const { can } = usePermissions();
  const msjFeatures = useMsjFeatures();
  // FAZ-37 (12 MD §5): serbest kapalı + görev açık → "Yeni" görev-önce moda
  // geçer — DM/grup HİÇ başlamaz (startDirect/startGroup çağrılmaz); kişi/birim
  // seçimi "Sohbet Başlat" ile görev-akışına gider.
  const gorevOnce = !msjFeatures.serbest && msjFeatures.gorev;

  const grupIzni = !gorevOnce && can("mesajlasma.group.create");
  // FAZ-68 — BİRİMLER YALNIZ SOHBET İÇİN.
  //
  // Kullanıcı (2026-07-29): "bir birimin tümüne görev atayınca gitmiyor zaten,
  // bu yüzden Yeni kısmından görev atama ve başvuru atma kısmından birimleri
  // kaldıralım… sohbet yapısı açıksa birimler görünsün ve birimlerle sadece
  // sohbet yapılabilsin."
  //
  // TEŞHİS (canlı veri, 2026-07-29): birime atanan görev kimsenin "Bana
  // Atananlar"ına düşmez; ayrı bir BİRİM HAVUZU kümesine girer ve o küme
  // `UnitMembership` satırlarından türer. Canlıda birimlerin çoğunda üyelik
  // YOK (Bench Birim 0, Temizlik 0, Zabıta 0…) → görev hiç kimseye ulaşmıyor,
  // sessizce havuzda kalıyordu. Yani sorun "atama çalışmıyor" değil, "hedefi
  // olmayan bir yere atanıyor". Kullanıcı deneyimi açısından ikisi aynı şey.
  //
  // Karar: görev/başvuru akışında birim seçeneği HİÇ ÇİZİLMEZ (çalışmayan bir
  // yolu göstermek, yokluğundan kötüdür). Backend'in birim ataması SÖKÜLMEDİ —
  // Görevler sayfasındaki hızlı form onu kullanmaya devam ediyor; burada
  // kapatılan yalnız bu panelin girişi.
  const birimIzni = !gorevOnce && can("mesajlasma.unit.send");

  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("kisiler"); // kisiler | birimler
  const [results, setResults] = useState({ users: [], units: [] });
  const [selected, setSelected] = useState([]); // {type, id, label, avatar}
  const [title, setTitle] = useState("");
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      searchContacts(query.trim())
        .then((d) => setResults({ users: d?.users || [], units: d?.units || [] }))
        .catch(() => setResults({ users: [], units: [] }));
    }, 250);
    return () => clearTimeout(timer.current);
  }, [query]);

  const toggle = (t) => {
    setSelected((prev) => {
      const varMi = prev.some((x) => x.type === t.type && x.id === t.id);
      if (varMi) return prev.filter((x) => !(x.type === t.type && x.id === t.id));
      if (gorevOnce) {
        // Görev-önce: birim TEK seçilir (aynı anda tek birime atama); kişi
        // çoklu seçilebilir (Hızlı Görev'de ilki sorumlu tutulur).
        if (t.type === "unit") return [...prev.filter((x) => x.type !== "unit"), t];
        return [...prev, t];
      }
      if (!grupIzni) return t.type === "user" ? [t] : prev;
      return [...prev, t];
    });
  };

  const users = selected.filter((s) => s.type === "user");
  const units = selected.filter((s) => s.type === "unit");
  const isGroup = users.length > 1 || units.length > 0;
  const canStart = selected.length > 0 && !starting;

  const start = async () => {
    if (!canStart) return;
    setStarting(true);
    try {
      const resp = isGroup
        ? await startGroup({ userIds: users.map((u) => u.id), unitId: units[0]?.id || null, title: title.trim() })
        : await startDirect(users[0].id);
      if (onStarted) onStarted(resp.conversation_id);
    } catch (e) {
      toast.error(e?.response?.data?.user_ids || e?.response?.data?.detail || "Sohbet başlatılamadı.");
    } finally {
      setStarting(false);
    }
  };

  // G9: görev-önce → sohbet OLUŞTURMADAN görev-akışını thread alanında aç.
  const startTaskFlow = () => {
    if (selected.length === 0 || !onStartTaskFlow) return;
    onStartTaskFlow({
      targets: users.map((u) => ({ id: u.id, display_name: u.label })),
      unitTarget: units[0] ? { id: units[0].id, name: units[0].label } : null,
    });
  };

  // B2: aktif sekmeye göre liste (izin yoksa birimler sekmesi zaten yok).
  const items =
    tab === "birimler" && birimIzni
      ? results.units.map((b) => ({ type: "unit", id: b.id, label: b.name }))
      : results.users.map(rehberSatiri);

  // FAZ-82 — yerleşim `rehberGruplari`nda (tek kaynak; "Kişi ekle" listesi de
  // aynı fonksiyonu kullanır). Birim sekmesi düz liste kalır.
  const bolumler = React.useMemo(
    () => (tab === "birimler" ? null : rehberBolumleri(items)),
    [items, tab]
  );

  const satir = (s) => {
    const secili = selected.some((x) => x.type === s.type && x.id === s.id);
    return (
      <button
        key={`${s.type}-${s.id}`}
        type="button"
        className="msj-new-item"
        onClick={() => toggle(s)}
        style={secili ? { background: "var(--color-accent-muted)" } : undefined}
      >
        {s.type === "unit" ? (
          <span className="msj-conv-groupava" style={{ width: 34, height: 34 }}>
            <BuildingOffice2Icon width={18} style={{ color: "var(--color-primary-soft)" }} />
          </span>
        ) : (
          <Avatar name={s.label} src={s.avatar} size={34} />
        )}
        <span className="grow">{s.label}</span>
        {/* FAZ-82: çip yalnız BİLGİ TAŞIDIĞI yerde. Kademeli+birim başlıklı
            listede her satıra "kişi" yazmak gürültüdür; kullanıcının istediği
            tek işaret saha çipidir ("saha personellerinin adının yanında
            küçük bir çiple saha yazın"). Birim satırı kendi etiketini korur. */}
        {s.type === "unit" ? (
          <span className="tag">birimin tümü</span>
        ) : (
          s.saha && <span className="msj-new-rozet">Saha</span>
        )}
      </button>
    );
  };

  return (
    <div className="msj-new">
      <div className="msj-list-head" style={{ padding: 0, border: "none" }}>
        {onCancel && (
          <button type="button" className="msj-iconbtn" style={{ width: 32, height: 32 }} onClick={onCancel} aria-label="Geri">
            <ArrowLeftIcon width={20} />
          </button>
        )}
        <span className="msj-list-title" style={{ flex: 1 }}>{gorevOnce ? "Talep Gönder" : "Yeni sohbet"}</span>
      </div>

      {birimIzni && (
        <div className="msj-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "kisiler"}
            className={`msj-tab ${tab === "kisiler" ? "is-active" : ""}`}
            onClick={() => setTab("kisiler")}
          >
            Kişiler
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "birimler"}
            className={`msj-tab ${tab === "birimler" ? "is-active" : ""}`}
            onClick={() => setTab("birimler")}
          >
            Birimler
          </button>
        </div>
      )}

      <input
        className="msj-new-search"
        type="text"
        value={query}
        autoFocus
        placeholder={tab === "birimler" ? "Birim ara…" : "Kişi ara…"}
        onChange={(e) => setQuery(e.target.value)}
      />

      {selected.length > 0 && (
        <div className="msj-new-chips">
          {selected.map((s) => (
            <button key={`${s.type}-${s.id}`} type="button" className="msj-new-chip" onClick={() => toggle(s)} title="Kaldır">
              {s.type === "unit" ? "🏢" : "👤"} {s.label} ×
            </button>
          ))}
        </div>
      )}

      {!gorevOnce && isGroup && (
        <input className="msj-new-search" type="text" value={title} maxLength={120} placeholder="Grup adı (opsiyonel)" onChange={(e) => setTitle(e.target.value)} />
      )}

      <div className="msj-new-results">
        {items.length === 0 && <div className="msj-empty">Sonuç yok</div>}
        {bolumler === null
          ? items.map(satir)
          : bolumler.map((b) => (
              <React.Fragment key={b.anahtar}>
                {/* baslik === null → makam kademeleri: başlık ÇİZİLMEZ
                    (kullanıcı: "gruplamaya gerek yok, yukarıda durmaları
                    yeterli; bir daha başlık eklemeyelim"). */}
                {b.baslik && <div className="msj-new-grup">{b.baslik}</div>}
                {b.kisiler.map(satir)}
              </React.Fragment>
            ))}
      </div>

      {gorevOnce ? (
        // G9: sohbet OLUŞMAZ — "Sohbet Başlat" görev-akışını thread alanında açar.
        <button
          type="button"
          className="btn-md btn-md-primary"
          style={{ justifyContent: "center" }}
          disabled={selected.length === 0}
          onClick={startTaskFlow}
        >
          Sohbet Başlat →
        </button>
      ) : (
        <button type="button" className="btn-md btn-md-primary" style={{ justifyContent: "center" }} disabled={!canStart} onClick={start}>
          {starting ? "Başlatılıyor…" : isGroup ? "Grup Sohbeti Başlat" : "Sohbeti Başlat"}
        </button>
      )}
    </div>
  );
}
